import { task } from 'ember-concurrency';
import { UploadFile } from 'ember-file-upload';
import _snakeCase from 'lodash/snakeCase';
import _sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
//@ts-expect-error
import ActionCell from 'volta/components/cell/action-cell';
//@ts-expect-error
import FormatCell from 'volta/components/cell/format-cell';
//@ts-expect-error
import IconCell from 'volta/components/cell/icon-cell';
// @ts-ignore
import TableNameCell from 'volta/components/import/cells/table-name-cell';
// @ts-ignore
import ResultCell from 'volta/components/import/cells/result-cell';
import InputFilter from 'volta/components/filters/input-filter';
//@ts-expect-error
import TagsCell from 'volta/components/cell/tags-cell';
import SelectFilter from 'volta/components/filters/select-filter';
import TagsFilter from 'volta/components/filters/tags-filter';
import ResourceController from 'volta/controllers/resource-controller';
import { ImportTableName } from 'volta/models/constants/import';
import ImportHisto from 'volta/models/import-histo';
import {
  FilesUploadState,
  IDataImportedSuccessfully,
  IDataImportFailed,
  IJobResult,
  IPersistedImportFile,
  TImportTableName
} from 'volta/models/types/data-import';
import { getDuration } from 'volta/utils/date-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { offsetFromPage, pageLimit, serializeFilters } from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type BvFlashService from 'volta/services/bv-flash';
import type EventStreamService from 'volta/services/event-stream';
import type StoreService from 'volta/services/store';
import type SessionUserService from 'volta/services/session-user';
import type LocalizationService from 'volta/services/localization';
import DateFilter from 'volta/components/filters/date-filter';
import StatusFilter from 'volta/components/filters/status-filter';

export const ImportStateMapping = {
  error: {
    index: 0,
    icon: 'close-circle',
    color: 'red',
    classNames: 'u-animation-fade-in',
    label: 'importsPage.statuses.error'
  },
  warning: {
    index: 1,
    icon: 'alert-circle',
    color: 'orange',
    classNames: 'u-animation-fade-in',
    label: 'importsPage.statuses.warning'
  },
  success: {
    index: 2,
    icon: 'checkmark-circle',
    color: 'green',
    classNames: 'u-animation-fade-in',
    label: 'importsPage.statuses.success'
  },
  importing: {
    index: 3,
    icon: 'sync',
    color: undefined,
    classNames: 'u-animation-spin',
    label: 'importsPage.statuses.importing'
  }
};

export const ImportsTableColumns = (intl: IntlService) => {
  return [
    {
      columnName: intl.t('table'),
      sortKey: 'tblName',
      valuePath: 'tblName',
      width: 190,
      minWidth: 50,
      cellComponent: TableNameCell
    },
    {
      columnName: intl.t('start'),
      sortKey: 'startedAt',
      valuePath: 'startedAt',
      width: 225,
      minWidth: 50,
      cellComponent: FormatCell,
      classNames: 'u-ellipsis',
      format: (model: ImportHisto) => {
        let startedAt = moment(model.startedAt).format('L HH:mm:ss');
        let endedAt = moment(model.endedAt).format('L HH:mm:ss');
        return {
          dataType: 'text',
          value: startedAt,
          suffix: moment(model.startedAt).from(moment()),
          tooltipContent: [
            {
              text: startedAt,
              subtext: intl.t('importsPage.startedAt')
            },
            !model.isLoading
              ? {
                  text: endedAt,
                  subtext: intl.t('importsPage.endedAt')
                }
              : { text: intl.t('importsPage.didNotEnd') }
          ]
        };
      }
    },
    {
      columnName: intl.t('duration'),
      valuePath: 'endedAt',
      isSortable: false,
      width: 50,
      minWidth: 50,
      classNames: 'u-ellipsis u-text-mono',
      format: (model: ImportHisto) => {
        return getDuration(model.startedAt, model.endedAt ?? moment());
      }
    },
    {
      columnName: intl.t('results'),
      valuePath: 'result',
      isSortable: false,
      width: 180,
      minWidth: 100,
      cellComponent: ResultCell
    },
    {
      columnName: intl.t('tags'),
      valuePath: 'tags',
      isSortable: false,
      width: 100,
      minWidth: 50,
      cellComponent: TagsCell
    }
  ];
};

const FilterDefinitions: TFilterDefinition[] = [
  {
    queryParam: 'q',
    apiParam: 'q',
    comparators: ['lk'],
    component: InputFilter,
    hidden: true,
    componentArgs: { inputType: 'text' }
  },
  {
    queryParam: 'tblName',
    apiParam: 'tblName',
    comparators: ['in'],
    isPrimary: true,
    component: SelectFilter,
    componentArgs: {
      possibleValues: ImportTableName.sort().map((key) => {
        return { key };
      })
    }
  },
  {
    queryParam: 'tags',
    apiParam: 'tags',
    comparators: ['any'],
    component: TagsFilter,
    isPrimary: true,
    componentArgs: { entityTypes: ['importHistos'] }
  },
  {
    queryParam: 'importedAt',
    apiParam: 'importedAt',
    comparators: ['bt'],
    component: DateFilter,
    componentArgs: {}
  },
  {
    queryParam: 'status',
    apiParam: 'status',
    comparators: ['in'],
    component: StatusFilter,
    componentArgs: {
      hideCount: true,
      possibleValues: Object.entries(ImportStateMapping).map(([value, item]) => {
        return { ...item, value, key: value };
      })
    }
  }
];

export default class ImportController extends ResourceController {
  @service intl!: IntlService;
  @service localization!: LocalizationService;
  @service sessionUser!: SessionUserService;
  @service store!: StoreService;
  @service eventStream!: EventStreamService;
  @service bvFlash!: BvFlashService;

  @tracked sort = '-startedAt';
  @tracked uploadedState: FilesUploadState = {};

  filterDefinitions = FilterDefinitions;

  get sortedFiles() {
    const indexedFiles = Object.entries(this.uploadedState).reduce(
      (acc: Record<string, Array<{ fileName: string; file?: UploadFile }>>, [fileName, state]) => {
        const name = state?.tableName ?? fileName;
        const entry = { fileName, file: state.file };
        if (acc[name]) {
          acc[name].push(entry);
        } else {
          acc[name] = [entry];
        }
        return acc;
      },
      {}
    );
    const sorted: Array<{ fileName: string; file?: UploadFile }> = [];

    ImportTableName.forEach((tableName) => {
      if (indexedFiles[tableName]) {
        sorted.push(..._sortBy(indexedFiles[tableName], 'fileName'));
        delete indexedFiles[tableName];
      }
    });
    sorted.push(..._sortBy(Object.values(indexedFiles).flat(), 'fileName'));
    return sorted;
  }

  get tableColumns() {
    return ImportsTableColumns(this.intl);
  }

  get uploadStateErrors() {
    return Object.entries(this.uploadedState).reduce((acc, [fileName, state]) => {
      if (state?.error) {
        acc[fileName] = state?.error;
      }

      return acc;
    }, {} as Record<string, string | string[] | undefined>);
  }

  @action
  handleFileAdd(file: UploadFile) {
    this.uploadedState = {
      ...this.uploadedState,
      [file.name]: { file, tableName: this.extractName(file) }
    };
  }

  @action
  handleRemoveFile(fileName: string) {
    delete this.uploadedState[fileName];
    this.uploadedState = { ...this.uploadedState };
  }

  @action
  validateFile(file: File) {
    const extension = file.name.split('.').lastObject;
    return extension?.toLowerCase() === 'csv' && file.size <= 500 * 1024 * 1024;
  }

  @action
  handleTableNameChange(fileName: string, tableName: TImportTableName) {
    this.uploadedState = {
      ...this.uploadedState,
      [fileName]: { ...this.uploadedState[fileName], tableName }
    };
  }

  uploadFileTask = task(
    { maxConcurrency: 1, enqueue: true },
    async (
      file: UploadFile,
      mapping: Record<string, string>,
      delimiter?: string,
      encoding?: string
    ) => {
      const state = this.uploadedState[file.name];

      if (!state) {
        return;
      }

      state.result = undefined;
      state.error = undefined;

      if (state.tableName) {
        try {
          const data = { mapping: JSON.stringify(mapping), delimiter, encoding };

          const uploadResult: IJobResult = await this.store.uploadFile(
            `jobs/import/async/csv/form/${state.tableName}`,
            file,
            {
              data
            }
          );

          if (uploadResult?.importId) {
            state.importHistoId = uploadResult.importId;
          }
        } catch (error) {
          defaultErrorHandler(error);
          state.error = this.intl.t('errors.uploadFailure');
        } finally {
          this.uploadedState = { ...this.uploadedState, [file.name]: state };
          return this.uploadedState;
        }
      } else {
        state.error = this.intl.t('importsPage.unknownTableName');
      }
      this.uploadedState = { ...this.uploadedState, [file.name]: state };
      return this.uploadedState;
    }
  );

  refreshHistory = task(async () => {
    const pageSize = Number(this.queryParamsValues.pageSize ?? '25');
    return this.resourceCollectionTask.perform({
      filter: serializeFilters(
        this.queryParamsValues as Record<string, string>,
        this.filterDefinitions
      ),
      page: {
        offset: offsetFromPage(Number(this.queryParamsValues.page ?? '1'), pageLimit(pageSize)),
        limit: pageSize
      },
      sort: this.sort
    });
  });

  // Called in the setupController hook of the route
  setup() {
    this.eventStream.addHandler(
      'dataImport',
      ['DataImportedSuccessfully'],
      this._onDataImportedSuccessfully,
      this
    );
    this.eventStream.addHandler('dataImport', ['DataImportFailed'], this._onDataImportFailed, this);
  }

  /**
   * Called in the resetController hook of the route, when exiting
   */
  teardown() {
    this.eventStream.removeHandlers(this);
    this.uploadedState = {};
  }

  fileURL(file: IPersistedImportFile) {
    return `importHistos/files/${file.bucket}/${file.key}`;
  }

  extractName(file: UploadFile): TImportTableName | undefined {
    let { name } = file;
    let normalizeKey = _snakeCase(name);
    return ImportTableName.reduce((prev, tableName: TImportTableName) => {
      if (normalizeKey.includes(tableName)) {
        return (prev && prev.length < tableName.length) || !prev ? tableName : prev;
      } else {
        return prev;
      }
    }, undefined as unknown as TImportTableName);
  }

  _onDataImportedSuccessfully(payload: IDataImportedSuccessfully) {
    if (this.isDestroying || this.isDestroyed) return;

    const [fileName, state] =
      Object.entries(this.uploadedState).find(
        ([_, st]) => st?.importHistoId === payload.importHistoId
      ) ?? [];

    // We only display the success message for the latest manually uploaded file
    // otherwise for real-time scheduled imports there would be flash messages all over
    if (fileName && state) {
      state.error = undefined;
      state.result = payload.result;
      this.uploadedState = { ...this.uploadedState, [fileName]: state };
    }
    this.refreshHistory.perform();
  }

  _onDataImportFailed(payload: IDataImportFailed) {
    if (this.isDestroying || this.isDestroyed) return;

    const [fileName, state] =
      Object.entries(this.uploadedState).find(
        ([_, st]) => st?.importHistoId === payload.importHistoId
      ) ?? [];

    if (fileName && state?.importHistoId === payload.importHistoId) {
      state.error = payload.isTechnical
        ? (this.intl.t('errors.import.internalError', { htmlSafe: true }) as string)
        : payload.error
        ? (this.localization.tOrElse(payload.error, undefined, {
            ...payload.errorContext,
            tableName: payload.tableName
          }) as string)
        : undefined;
      state.result = undefined;
      this.uploadedState = { ...this.uploadedState, [fileName]: state };
      this.refreshHistory.perform();
    }
  }
}
